// B2.2 — compact markdown → DOM renderer for corpus documents. The section
// dispatcher renders template-defined H2 sections as typed components;
// everything else lands here as styled prose (the GL fallback rule: styled
// prose is CORRECT for novel sections, not a defect). Escape-first: raw
// text is HTML-escaped before any inline transform, so corpus content can
// never inject markup. Covers the corpus template vocabulary — headings,
// lists (nested two levels), fenced code, tables, blockquotes, task lists,
// bold/italic/code/links, hr — not full CommonMark.

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Inline transforms over ALREADY-ESCAPED text. */
function inline(s: string): string {
  let out = s;
  // code spans first so their contents skip emphasis/link transforms
  out = out.replace(/`([^`]+)`/g, (_m, c: string) => `<code>${c}</code>`);
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[\s(])\*([^*]+)\*(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text: string, href: string) => {
    const safe = /^(https?:|#|\/)/.test(href) ? href : '#';
    const ext = /^https?:/.test(safe) ? ' target="_blank" rel="noopener noreferrer"' : '';
    return `<a href="${safe}"${ext}>${text}</a>`;
  });
  // corpus cross-links: [[slug]] → file-viewer-ish anchor (consumer wires clicks)
  out = out.replace(/\[\[([a-z0-9-]+)\]\]/g, '<a class="xref" data-xref="$1">$1</a>');
  return out;
}

export interface MdOptions {
  /** Base corpus path of the document — relative md links/images resolve
   * against it onto the /corpus/ static route. */
  docPath?: string;
  /** Chat-lane line semantics: a single newline inside a paragraph renders as
   * a hard <br> instead of collapsing to a space (CommonMark soft break).
   * Corpus documents keep the default; the pi chat view opts in so a terse
   * agent's line-separated output keeps its structure. */
  chatBreaks?: boolean;
}

/** Renders markdown into a detached container. */
export function renderMarkdown(src: string, opts: MdOptions = {}): HTMLElement {
  const root = document.createElement('div');
  root.className = 'md';
  // HTML comments are authoring guidance (template scaffolding), not content —
  // markdown drops them; rendering them escaped would show them as prose.
  const lines = src.replace(/<!--[\s\S]*?-->/g, '').replace(/\r\n/g, '\n').split('\n');
  let i = 0;

  const dir = opts.docPath ? opts.docPath.replace(/\/[^/]*$/, '') : '';
  const resolveRel = (href: string): string => {
    if (/^(https?:|#|\/|data:)/.test(href)) return href;
    return `/corpus/${dir ? `${dir}/` : ''}${href}`.replace(/\/\.\//g, '/');
  };

  const flushPara = (buf: string[]): void => {
    if (buf.length === 0) return;
    const p = document.createElement('p');
    // chatBreaks: per-line transforms + <br> joins (an inline span crossing a
    // line break is sacrificed — chat text doesn't wrap emphasis across lines).
    p.innerHTML = opts.chatBreaks
      ? buf.map((l) => inline(esc(l))).join('<br>')
      : inline(esc(buf.join(' ')));
    root.appendChild(p);
    buf.length = 0;
  };

  const para: string[] = [];
  while (i < lines.length) {
    const line = lines[i]!;

    // fenced code
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      flushPara(para);
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i]!)) {
        buf.push(lines[i]!);
        i++;
      }
      i++; // closing fence
      const panel = document.createElement('div');
      panel.className = 'code-panel';
      const pre = document.createElement('pre');
      const code = document.createElement('code');
      code.textContent = buf.join('\n');
      if (fence[1]) code.dataset.lang = fence[1];
      pre.appendChild(code);
      panel.appendChild(pre);
      root.appendChild(panel);
      continue;
    }

    // blank
    if (/^\s*$/.test(line)) {
      flushPara(para);
      i++;
      continue;
    }

    // headings
    const h = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (h) {
      flushPara(para);
      const el = document.createElement(`h${Math.min(6, h[1]!.length + 1)}`); // demote: doc h1 → h2 etc. (page owns h1)
      el.innerHTML = inline(esc(h[2]!));
      root.appendChild(el);
      i++;
      continue;
    }

    // hr
    if (/^(-{3,}|\*{3,})\s*$/.test(line)) {
      flushPara(para);
      root.appendChild(document.createElement('hr'));
      i++;
      continue;
    }

    // blockquote
    if (/^>\s?/.test(line)) {
      flushPara(para);
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i]!)) {
        buf.push(lines[i]!.replace(/^>\s?/, ''));
        i++;
      }
      const bq = document.createElement('blockquote');
      bq.appendChild(renderMarkdown(buf.join('\n'), opts));
      root.appendChild(bq);
      continue;
    }

    // table
    if (/^\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\|[\s:|-]+\|\s*$/.test(lines[i + 1]!)) {
      flushPara(para);
      const headerCells = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\|.*\|\s*$/.test(lines[i]!)) {
        rows.push(splitRow(lines[i]!));
        i++;
      }
      const wrap = document.createElement('div');
      wrap.className = 'tbl-wrap';
      const table = document.createElement('table');
      table.className = 'data';
      const thead = document.createElement('thead');
      const trh = document.createElement('tr');
      for (const c of headerCells) {
        const th = document.createElement('th');
        th.innerHTML = inline(esc(c));
        trh.appendChild(th);
      }
      thead.appendChild(trh);
      const tbody = document.createElement('tbody');
      for (const r of rows) {
        const tr = document.createElement('tr');
        for (const c of r) {
          const td = document.createElement('td');
          td.innerHTML = inline(esc(c));
          tr.appendChild(td);
        }
        tbody.appendChild(tr);
      }
      table.append(thead, tbody);
      wrap.appendChild(table);
      root.appendChild(wrap);
      continue;
    }

    // lists (ordered/unordered, one nesting level via 2+ space indent)
    if (/^(\s*)([-*+]|\d+[.)])\s+/.test(line)) {
      flushPara(para);
      root.appendChild(renderList(lines, () => i, (n) => (i = n), opts));
      continue;
    }

    // image on its own line
    const img = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/.exec(line);
    if (img) {
      flushPara(para);
      const fig = document.createElement('figure');
      const el = document.createElement('img');
      el.src = resolveRel(img[2]!);
      el.alt = img[1] ?? '';
      el.loading = 'lazy';
      fig.appendChild(el);
      if (img[1]) {
        const cap = document.createElement('figcaption');
        cap.className = 'lbl';
        cap.textContent = img[1];
        fig.appendChild(cap);
      }
      root.appendChild(fig);
      i++;
      continue;
    }

    para.push(line.trim());
    i++;
  }
  flushPara(para);
  return root;
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim());
}

function renderList(lines: string[], getI: () => number, setI: (n: number) => void, opts: MdOptions): HTMLElement {
  const itemRe = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
  let i = getI();
  const first = itemRe.exec(lines[i]!)!;
  const baseIndent = first[1]!.length;
  const ordered = /\d/.test(first[2]!);
  const list = document.createElement(ordered ? 'ol' : 'ul');
  list.className = 'tight';

  while (i < lines.length) {
    const m = itemRe.exec(lines[i]!);
    if (!m || m[1]!.length < baseIndent) break;
    if (m[1]!.length > baseIndent) {
      // nested list under the previous item
      const prev = list.lastElementChild;
      if (prev) {
        setI(i);
        prev.appendChild(renderList(lines, getI, setI, opts));
        i = getI();
        continue;
      }
    }
    const li = document.createElement('li');
    let text = m[3]!;
    i++;
    // Join the item's continuation lines (indented OR lazy col-0 wraps) into
    // ONE logical text BEFORE inline rendering — per-line rendering broke
    // emphasis that spans a source wrap (`**bold` / `text**` on separate
    // lines showed raw asterisks) and forced visual line breaks instead of
    // reflow. A blank line or new block structure ends the item.
    while (i < lines.length && !itemRe.test(lines[i]!)) {
      const cont = lines[i]!;
      if (/^\s*$/.test(cont)) break;
      if (/^(#{1,6})\s|^```|^>\s?|^\|/.test(cont)) break;
      text += ` ${cont.trim()}`;
      i++;
    }
    // task list
    const task = /^\[( |x|X)\]\s+(.*)$/.exec(text);
    if (task) {
      const done = task[1] !== ' ';
      li.className = done ? 'task done' : 'task';
      text = task[2]!;
      const mark = document.createElement('span');
      mark.className = 'task-mark';
      mark.textContent = done ? '✓' : '';
      li.appendChild(mark);
      const span = document.createElement('span');
      span.innerHTML = inline(esc(text));
      li.appendChild(span);
    } else {
      li.innerHTML = inline(esc(text));
    }
    list.appendChild(li);
  }
  setI(i);
  return list;
}

/** Splits a markdown document into { frontmatter, body } — frontmatter is
 * returned raw (the server usually parses it; this is for the raw toggle). */
export function splitFrontmatter(src: string): { frontmatter: string | null; body: string } {
  const m = /^---\n([\s\S]*?)\n---\n?/.exec(src);
  if (!m) return { frontmatter: null, body: src };
  return { frontmatter: m[1]!, body: src.slice(m[0].length) };
}

/** Splits a body into H2 sections for the section dispatcher:
 * [{heading, content}] with a leading pre-section under heading ''.
 * Fence-aware: a `## ` line inside ``` code never splits. */
export function splitH2Sections(body: string): { heading: string; content: string }[] {
  const out: { heading: string; content: string }[] = [];
  const lines = body.split('\n');
  let heading = '';
  let buf: string[] = [];
  let inFence = false;
  for (const line of lines) {
    if (/^```/.test(line)) inFence = !inFence;
    const m = inFence ? null : /^##\s+(.+?)\s*#*\s*$/.exec(line);
    if (m) {
      out.push({ heading, content: buf.join('\n') });
      heading = m[1]!;
      buf = [];
    } else {
      buf.push(line);
    }
  }
  out.push({ heading, content: buf.join('\n') });
  if (out.length > 1 && out[0]!.heading === '' && out[0]!.content.trim() === '') out.shift();
  return out;
}
